import {
  Box,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import type { IconType } from "react-icons"
import { FiArrowRight } from "react-icons/fi"

export type ProductionNavigationDestination =
  | "/WMS/technologies/technologies-list"
  | "/WMS/technologies/tree"
  | "/WMS/technologies/flows"
  | "/WMS/production-orders/production-orders-list"
  | "/WMS/production-orders/create"
  | "/WMS/production-orders/chain"
  | "/WMS/auto-replenishment/overview"
  | "/WMS/auto-replenishment/policies"
  | "/WMS/auto-replenishment/requests"

export type ProductionModuleCard = {
  title: string
  description: string
  to: ProductionNavigationDestination
  icon: IconType
}

type ProductionModuleHubPageProps = {
  title: string
  description: string
  cards: ProductionModuleCard[]
}

export function ProductionModuleHubPage({
  title,
  description,
  cards,
}: ProductionModuleHubPageProps) {
  const cardBg = useColorModeValue("white", "gray.800")
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.300")
  const mutedText = useColorModeValue("gray.600", "gray.300")
  const iconBg = useColorModeValue("green.50", "whiteAlpha.100")
  const iconColor = useColorModeValue("green.600", "green.200")

  return (
    <Container maxW="7xl" py={{ base: 5, md: 8 }}>
      <Stack spacing={2} mb={{ base: 6, md: 8 }}>
        <Heading size="lg" data-testid="production-module-title">
          {title}
        </Heading>
        <Text color={mutedText} maxW="3xl">
          {description}
        </Text>
      </Stack>

      {cards.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
          {cards.map((card) => (
            <Box
              key={card.to}
              as={Link}
              to={card.to}
              aria-label={`${card.title} — otwórz`}
              data-testid="production-module-card"
              display="flex"
              flexDirection="column"
              minH="210px"
              p={5}
              bg={cardBg}
              borderWidth="1px"
              borderColor={cardBorder}
              borderRadius="xl"
              color="inherit"
              textDecoration="none"
              transition="transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease"
              _hover={{
                textDecoration: "none",
                transform: "translateY(-2px)",
                boxShadow: "md",
                borderColor: iconColor,
              }}
              _focusVisible={{
                outline: "3px solid",
                outlineColor: iconColor,
                outlineOffset: "2px",
              }}
            >
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="44px"
                h="44px"
                borderRadius="lg"
                bg={iconBg}
                color={iconColor}
                mb={5}
              >
                <Icon as={card.icon} boxSize={5} />
              </Box>

              <Heading size="md" mb={2}>
                {card.title}
              </Heading>
              <Text color={mutedText} lineHeight="tall">
                {card.description}
              </Text>

              <HStack mt="auto" pt={5} color={iconColor} fontWeight="700">
                <Text>Otwórz</Text>
                <Icon as={FiArrowRight} aria-hidden />
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      ) : (
        <Box
          p={5}
          bg={cardBg}
          borderWidth="1px"
          borderColor={cardBorder}
          borderRadius="xl"
        >
          <Text color={mutedText}>
            Brak dostępnych obszarów dla bieżących uprawnień.
          </Text>
        </Box>
      )}
    </Container>
  )
}
